import { useCallback, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"
import api from "../../lib/api"
import { toastError, toastSuccess } from "../../lib/toast"
import ModuleLayout from "../../components/admin/ModuleLayout"
import DataTable, { type DataTableColumn } from "../../components/ui/table/DataTable"
import { btnPrimary, btnSecondary } from "../../components/admin/FormComponents"
import { downloadJson, type Summary } from "./section-quiz-test/model"

// Historial de pruebas de un examen — openspec/changes/section-quiz-expert-test.

type RunRow = {
  id: string
  status: "in_progress" | "graded"
  source: "live" | "backup"
  created_at: string
  submitted_at: string | null
  created_by: string | null
  summary: Summary | null
}

const PAGE_SIZE = 20

export default function SectionQuizTestsPage() {
  const { sectionQuizId } = useParams<{ sectionQuizId: string }>()
  const navigate = useNavigate()
  const [runs, setRuns] = useState<RunRow[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [pendingDelete, setPendingDelete] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!sectionQuizId) return
    setLoading(true)
    try {
      const res = await api.get(`/academy/section-quizzes/${sectionQuizId}/test/runs`)
      setRuns(res.data.data ?? [])
    } catch (err) {
      toastError(err)
    } finally {
      setLoading(false)
    }
  }, [sectionQuizId])

  useEffect(() => { void load() }, [load])

  const remove = async (id: string) => {
    try {
      await api.delete(`/academy/section-quizzes/test/runs/${id}`)
      toastSuccess("Prueba eliminada")
      setPendingDelete(null)
      await load()
    } catch (err) {
      toastError(err)
    }
  }

  const exportAll = async () => {
    if (!sectionQuizId) return
    try {
      const res = await api.get("/academy/section-quizzes/test/export", { params: { section_quiz_id: sectionQuizId } })
      const { grading, validation } = res.data.data as { grading: unknown[]; validation: unknown[] }
      if (!grading.length && !validation.length) {
        toastError(new Error("Ninguna prueba tiene feedback todavía."))
        return
      }
      if (grading.length) downloadJson(`feedback-examen-calificacion.json`, grading)
      if (validation.length) downloadJson(`feedback-examen-validacion-claves.json`, validation)
      toastSuccess("Exportado en el formato del benchmark")
    } catch (err) {
      toastError(err)
    }
  }

  const graded = runs.filter((r) => r.status === "graded")
  const labeled = graded.reduce((n, r) => n + (r.summary?.labeled ?? 0), 0)
  const disagreements = graded.reduce((n, r) => n + (r.summary?.disagreements ?? 0), 0)

  const columns: DataTableColumn<RunRow>[] = [
    {
      key: "created_at",
      label: "Fecha",
      render: (r) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{new Date(r.created_at).toLocaleString("es-CO")}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{r.created_by ?? "—"} · {r.source === "live" ? "generado ahora" : "banco de respaldo"}</p>
        </div>
      ),
    },
    {
      key: "status",
      label: "Estado",
      render: (r) => (
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.status === "graded" ? "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300" : "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"}`}>
          {r.status === "graded" ? "Calificada" : "Sin enviar"}
        </span>
      ),
    },
    { key: "disagreements", label: "Desacuerdos", render: (r) => (r.summary ? String(r.summary.disagreements) : "—") },
    {
      key: "labeled",
      label: "Evaluadas por ti",
      render: (r) => (r.summary ? `${r.summary.labeled}${r.summary.keyIssues ? ` · ${r.summary.keyIssues} clave(s) con problema` : ""}` : "—"),
    },
    {
      key: "actions",
      label: "",
      headerAriaLabel: "Acciones",
      align: "right",
      render: (r) => (
        <div className="flex justify-end gap-2">
          {r.status === "graded" && (
            <button type="button" className={btnSecondary} onClick={() => navigate(`/instructor/section-quizzes/tests/${r.id}`)}>Abrir</button>
          )}
          {pendingDelete === r.id ? (
            <>
              <button type="button" className={btnPrimary} onClick={() => void remove(r.id)}>Confirmar</button>
              <button type="button" className={btnSecondary} onClick={() => setPendingDelete(null)}>Cancelar</button>
            </>
          ) : (
            <button type="button" className={btnSecondary} onClick={() => setPendingDelete(r.id)}>Eliminar</button>
          )}
        </div>
      ),
    },
  ]

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Contenido", { label: "Exámenes IA", to: "/instructor/section-quizzes" }, "Pruebas"]}
      title="Pruebas del examen"
      subtitle="Tus pruebas de este examen. No cuentan en estadísticas, tiempo de espera ni completitud."
      actions={
        <>
          <button type="button" className={btnSecondary} onClick={() => void exportAll()}>Exportar todo el feedback</button>
          <button type="button" className={btnPrimary} onClick={() => navigate(`/instructor/section-quizzes/${sectionQuizId}/test`)}>Nueva prueba</button>
        </>
      }
      tabs={[]}
      metrics={[
        { label: "Pruebas", value: String(runs.length) },
        { label: "Respuestas evaluadas por ti", value: String(labeled) },
        { label: "Desacuerdos entre calificadores", value: String(disagreements) },
      ]}
    >
      <DataTable
        columns={columns}
        rows={runs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)}
        rowKey={(r) => r.id}
        loading={loading}
        emptyMessage="Aún no has probado este examen."
        sort={null}
        onSortChange={() => {}}
        page={page}
        pageSize={PAGE_SIZE}
        total={runs.length}
        onPageChange={setPage}
      />
    </ModuleLayout>
  )
}
